import React, { memo } from "react";
import { ReactComponent as ToDockIcon } from "../../assets/to-dock.svg";
import { ReactComponent as StopIcon } from "../../assets/stop.svg";
import { ReactComponent as AnimatedVehicleIcon } from "../../assets/animated-vehicle.svg";

import {
  ListItemStyled,
  ListItemContentStyled,
  VerticalSeparatorStyled,
  MissionStatusIconContainerStyled,
  MissionStatusIconBgStyled,
  MissionStatusIconStyled,
  StatusBadgeStyled,
  ListItemDividerStyled,
  InProgressListItemContentStyled,
  InProgressInfoSectionStyled,
  InProgressMissionStatusStyled,
  ProgressBarStyled,
  StationMarkerStyled,
  StopShapeStyled,
  StationLabelStyled,
  StationLabelTextStyled,
  ProgressLineStyled,
  VehicleProgressIndicatorStyled,
  VehicleProgressIconStyled,
  CyclesContainerStyled,
  CyclesFrameStyled,
  CyclesCounterStyled,
  CyclesCurrentStyled,
  CyclesSeparatorStyled,
  CyclesTotalStyled,
  CyclesLabelStyled,
} from "../../styles";
import { VehicleInfo } from "../VehicleInfo/VehicleInfo";

export interface InProgressVehicleListItemData {
  id: string;
  name: string;
  imageUrl?: string;
  batteryPercent: number; // 0-100 (raw percentage from vehicle.batteryLevel)
  missionText: string;
  // DEV-3706 (In Progress parity): vehicle's live online status is OFFLINE.
  // The row stays visible (a running mission shouldn't vanish) but is greyed
  // and its status badge reads "Offline".
  isOffline?: boolean;
  startStation: string; // Station name/label (e.g., "S1")
  endStation: string; // Station name/label (e.g., "S2")
  progress: number; // 0-100 (percentage of progress)
  currentCycle: number;
  totalCycles: number;
  // On-screen travel direction of the indicator along the distance line, used
  // to slide the row out in that direction when it leaves the list. "right" for
  // departing/continuing (FROM) legs, "left" for approaching (TO) legs.
  travelDirection?: "left" | "right";
}

interface InProgressVehicleListItemProps {
  vehicle: InProgressVehicleListItemData;
  isLast?: boolean;
  // The row is leaving the In Progress list — play the exit animation rather
  // than unmount abruptly (managed by useExitTransition in StatusTabs).
  isExiting?: boolean;
}

export const InProgressVehicleListItem = memo<InProgressVehicleListItemProps>(
  ({ vehicle, isLast = false, isExiting = false }) => {
    const {
      name,
      imageUrl,
      missionText,
      isOffline = false,
      startStation,
      endStation,
      progress,
      currentCycle,
      totalCycles,
      travelDirection = "right",
    } = vehicle;

    return (
      <ListItemStyled
        $isLast={isLast}
        $offline={isOffline}
        $exiting={isExiting}
        $exitDir={travelDirection}
        data-test-id={`in-progress-list-item-${vehicle.id}`}
      >
        <ListItemContentStyled>
          <InProgressListItemContentStyled>
            <VehicleInfo
              name={name}
              imageUrl={imageUrl}
              batteryPercent={vehicle.batteryPercent}
            />

            <InProgressInfoSectionStyled>
              <VerticalSeparatorStyled />

              <InProgressMissionStatusStyled
                data-test-id={`in-progress-mission-status-${vehicle.id}`}
              >
                <MissionStatusIconContainerStyled>
                  <MissionStatusIconBgStyled $bgColor="#C7DAF9" />
                  <MissionStatusIconStyled>
                    <ToDockIcon />
                  </MissionStatusIconStyled>
                </MissionStatusIconContainerStyled>
                <StatusBadgeStyled
                  $variant={isOffline ? "offline" : undefined}
                  data-test-id={`in-progress-mission-text-${vehicle.id}`}
                >
                  {isOffline ? "Offline" : missionText}
                </StatusBadgeStyled>
              </InProgressMissionStatusStyled>

              <ProgressBarStyled
                data-test-id={`in-progress-progress-bar-${vehicle.id}`}
              >
                <StationMarkerStyled
                  data-test-id={`in-progress-start-station-${vehicle.id}`}
                >
                  <StopShapeStyled>
                    <StopIcon />
                  </StopShapeStyled>
                  <StationLabelStyled>
                    <StationLabelTextStyled>
                      {startStation}
                    </StationLabelTextStyled>
                  </StationLabelStyled>
                </StationMarkerStyled>

                <ProgressLineStyled />

                <VehicleProgressIndicatorStyled
                  $progress={progress}
                  data-test-id={`in-progress-vehicle-indicator-${vehicle.id}`}
                >
                  <VehicleProgressIconStyled>
                    <AnimatedVehicleIcon />
                  </VehicleProgressIconStyled>
                </VehicleProgressIndicatorStyled>

                <StationMarkerStyled
                  data-test-id={`in-progress-end-station-${vehicle.id}`}
                >
                  <StopShapeStyled>
                    <StopIcon />
                  </StopShapeStyled>
                  <StationLabelStyled style={{ right: "1px", left: "auto" }}>
                    <StationLabelTextStyled>
                      {endStation}
                    </StationLabelTextStyled>
                  </StationLabelStyled>
                </StationMarkerStyled>
              </ProgressBarStyled>
            </InProgressInfoSectionStyled>

            <CyclesContainerStyled
              data-test-id={`in-progress-cycles-${vehicle.id}`}
            >
              <CyclesFrameStyled>
                <CyclesCounterStyled>
                  <CyclesCurrentStyled
                    data-test-id={`in-progress-current-cycle-${vehicle.id}`}
                  >
                    {currentCycle}
                  </CyclesCurrentStyled>
                  <CyclesSeparatorStyled />
                  <CyclesTotalStyled
                    data-test-id={`in-progress-total-cycles-${vehicle.id}`}
                  >
                    {totalCycles}
                  </CyclesTotalStyled>
                </CyclesCounterStyled>
                <CyclesLabelStyled>Cycles</CyclesLabelStyled>
              </CyclesFrameStyled>
            </CyclesContainerStyled>
          </InProgressListItemContentStyled>
        </ListItemContentStyled>
        {!isLast && <ListItemDividerStyled />}
      </ListItemStyled>
    );
  }
);

InProgressVehicleListItem.displayName = "InProgressVehicleListItem";
